import { db } from "./DBHelper";
import CustomerService from "services/CustomerService";
import DateTimeHelpers from "./DateTimeHelpers";
import { STORAGE_KEYS, COLLECTIONS, SALE_SYNC_STATUS, CUSTOMER_JOURNAL_TYPES, EVENTS } from "../const";
import { eventsEmitter } from "utils/events";
const CUSTOMERS_CACHE_DURATION_IN_MS = 24 * 60 * 60 * 1000;

const CustomerHelper = {
  saveCustomers: (list) => db.addAll(COLLECTIONS.CUSTOMERS.name, list),

  getCustomersFromDB: () => db.getAll(COLLECTIONS.CUSTOMERS.name),

  getCustomerByLocalIdFromDB: (localId) => db.get(COLLECTIONS.CUSTOMERS.name, localId),

  getCustomerByIdFromDB: async (id) => {
    const customers = await db.filter(COLLECTIONS.CUSTOMERS.name, { id });
    return customers[0] || null;
  },

  getCustomerByAnyIdFromDB: async (id, localId) => {
    const customerById = await CustomerHelper.getCustomerByIdFromDB(id);
    if (customerById) return customerById;
    return CustomerHelper.getCustomerByLocalIdFromDB(localId);
  },

  saveCustomerGroups: (list) => db.addAll(COLLECTIONS.CUSTOMER_GROUPS.name, list),

  getCustomerGroupsFromDB: () => db.getAll(COLLECTIONS.CUSTOMER_GROUPS.name),

  addCustomerToDB: (customer) => db.add(COLLECTIONS.CUSTOMERS.name, customer),

  updateCustomerInDB: (customer) => db.update(COLLECTIONS.CUSTOMERS.name, customer.local_id, customer),

  loadInitialCustomers: async (syncFromBE = false) => {
    const now = Date.now();
    const customersLastSyncedAt = Number(localStorage.getItem(STORAGE_KEYS.LAST_CUSTOMER_FETCH_TIME));

    const cacheExpired = !customersLastSyncedAt || now - customersLastSyncedAt > CUSTOMERS_CACHE_DURATION_IN_MS;

    if (!cacheExpired && !syncFromBE) {
      const cachedCustomers = await CustomerHelper.getCustomersFromDB();
      const cachedCustomerGroups = await CustomerHelper.getCustomerGroupsFromDB();

      if (cachedCustomers.length) {
        return {
          customers: cachedCustomers,
          customerGroups: cachedCustomerGroups,
          fromCache: true,
        };
      }
    }

    try {
      const [customers, customerGroups] = await Promise.all([
        CustomerService.getCustomers(),
        CustomerService.getCustomerGroups(),
      ]);
      const rawCustomers = Array.isArray(customers) ? customers : customers?.results || [];

      if (!Array.isArray(rawCustomers)) {
        throw new Error("Invalid format for customers data");
      }

      const updatedCustomers = rawCustomers;
      const updatedCustomerGroups = customerGroups;

      await Promise.all([
        CustomerHelper.saveCustomers(updatedCustomers),
        CustomerHelper.saveCustomerGroups(updatedCustomerGroups),
      ]);

      localStorage.setItem(STORAGE_KEYS.LAST_CUSTOMER_FETCH_TIME, String(now));

      return {
        customers: updatedCustomers,
        customerGroups: updatedCustomerGroups,
        fromCache: false,
      };
    } catch (error) {
      const cachedCustomers = await CustomerHelper.getCustomersFromDB();
      const cachedCustomerGroups = await CustomerHelper.getCustomerGroupsFromDB();

      if (cachedCustomers.length) {
        return {
          customers: cachedCustomers,
          customerGroups: cachedCustomerGroups,
          fromCache: true,
        };
      }

      throw new Error("No customer data available from API or IndexedDB");
    }
  },

  isCustomerEligibleForCredit: (customer, requestedAmount = 0) => {
    if (!customer) {
      return { eligible: false, reason: "No customer selected." };
    }
    const availableCredit = CustomerHelper.getAvailableCredit(customer);
    const isEligible = requestedAmount <= availableCredit;

    return { eligible: isEligible, reason: isEligible ? "Eligible" : "Insufficient credit limit" };
  },

  getAvailableCredit: (customer) => {
    if (!customer) return 0;
    const balance = Number(customer.balance) || 0;
    const creditLimit = Number(customer.credit_limit) || 0;

    return Math.max(0, balance - creditLimit);
  },

  getLoyaltyStatus: (customer) => {
    if (!customer) return { card: "-", points: 0 };
    return {
      card: customer.loyalty_card_no ?? "N/A",
      points: customer.points || 0,
    };
  },

  getUpdatedCustomerBalance: (customer, creditAmountUsed) => {
    if (!customer || creditAmountUsed === 0) return null;

    const currentBalance = Number(customer.balance) || 0;
    const newBalance = currentBalance - creditAmountUsed;

    return {
      ...customer,
      balance: newBalance,
    };
  },

  syncPendingCustomers: async () => {
    try {
      const pendingCustomers = await db.filter(COLLECTIONS.CUSTOMERS.name, {
        sync_status: SALE_SYNC_STATUS.PENDING,
      });

      if (pendingCustomers.length > 0) {
        for (const customer of pendingCustomers) {
          try {
            const apiData = { ...customer };
            delete apiData.local_id;
            delete apiData.sync_status;
            const synced = await CustomerService.createCustomer(apiData);
            if (synced && synced.id) {
              const finalCustomer = {
                ...synced,
                local_id: customer.local_id,
                sync_status: SALE_SYNC_STATUS.SYNCED_TO_CLOUD,
              };
              await CustomerHelper.updateCustomerInDB(finalCustomer);
            }
          } catch (err) {
            console.error(`Failed to sync customer ${customer.name || ""}:`, err);
          }
        }
      }
    } catch (err) {
      console.error("Failed to sync pending customers:", err);
    }
  },

  buildCustomerPaymentPayload: (log) => ({
    credit: Number(log.credit || 0),
    note: log.description || "",
    shift_id: log.shift_id ? Number(log.shift_id) : null,
    tenders: (log.tenders || []).map((t) => ({
      amount: Number(t.amount || 0),
      payment_mode: {
        id: t.payment_mode?.id,
        is_active: t.payment_mode?.is_active ?? t.payment_mode?.is_enabled ?? true,
        name: t.payment_mode?.name || "",
        order: t.payment_mode?.order ?? 1,
      },
    })),
  }),

  syncPendingCustomerLogs: async () => {
    try {
      const pendingLogs = await db.filter(COLLECTIONS.CUSTOMER_PAYMENT_LOGS.name, {
        sync_status: [SALE_SYNC_STATUS.PENDING, SALE_SYNC_STATUS.FAILED],
      });

      if (pendingLogs.length > 0) {
        for (const log of pendingLogs) {
          try {
            const existingCustomers = await CustomerHelper.getCustomersFromDB();
            const customer = existingCustomers.find(
              (c) => Number(c.local_id) === Number(log.customer_id) || Number(c.id) === Number(log.customer_id),
            );
            if (!customer || !customer.id) {
              console.warn(`Cannot sync customer log ${log.local_id} because customer has no backend ID yet`);
              continue;
            }

            const data = CustomerHelper.buildCustomerPaymentPayload(log);

            const res = await CustomerService.addPaymentToCustomer(customer.id, data);
            if (res) {
              await db.update(COLLECTIONS.CUSTOMER_PAYMENT_LOGS.name, log.local_id, {
                ...log,
                sync_status: SALE_SYNC_STATUS.SYNCED_TO_CLOUD,
              });

              const updatedCustomer = await CustomerService.getCustomer(customer.id);
              if (updatedCustomer) {
                updatedCustomer.local_id = customer.local_id;
                await CustomerHelper.updateCustomerInDB(updatedCustomer);
              }
            }
          } catch (err) {
            console.error(`Failed to sync customer log ${log.local_id}:`, err);
            await db.update(COLLECTIONS.CUSTOMER_PAYMENT_LOGS.name, log.local_id, {
              ...log,
              sync_status: SALE_SYNC_STATUS.FAILED,
              sync_message: err.message || "Failed to sync",
            });
          }
        }
      }
    } catch (err) {
      console.error("Failed to sync pending customer logs:", err);
    }
  },

  saveOrUpdateCustomerOffline: async (customerData) => {
    const existing = await CustomerHelper.getCustomerByAnyIdFromDB(customerData.id, customerData.local_id);
    if (existing) {
      const updated = { ...customerData, local_id: existing.local_id };
      await CustomerHelper.updateCustomerInDB(updated);
      return updated;
    } else {
      const local_id = await CustomerHelper.addCustomerToDB(customerData);
      return { ...customerData, local_id };
    }
  },

  fetchAndUpdateCustomerFromBE: async (customerId) => {
    if (!customerId || Number(customerId) <= 0) return null;
    try {
      const customer = await CustomerService.getCustomer(customerId);
      if (customer && (customer.id || customer.mobile)) {
        const customerFromDB = await CustomerHelper.saveOrUpdateCustomerOffline(customer);
        eventsEmitter.emit(EVENTS.CUSTOMER_UPDATED, customerFromDB);
        return customerFromDB;
      }
    } catch (err) {
      console.warn(`Failed to fetch and update customer #${customerId} from BE:`, err);
    }
    return null;
  },

  getUnsyncedSalesForCustomer: async (customerId) => {
    const allSales = await db.getAll(COLLECTIONS.SALES.name);
    return allSales.filter(
      (s) =>
        (Number(s.customer?.id) === Number(customerId) || Number(s.customer?.local_id) === Number(customerId)) &&
        s.sync_status !== SALE_SYNC_STATUS.SYNCED_TO_CLOUD &&
        Number(s.customer_credit_used || 0) > 0,
    );
  },

  getLocalSalesForCustomer: async (customerId) => {
    const allSales = await db.getAll(COLLECTIONS.SALES.name);
    return allSales.filter(
      (s) => Number(s.customer?.id) === Number(customerId) || Number(s.customer?.local_id) === Number(customerId),
    );
  },

  getUnsyncedPaymentsForCustomer: async (customerId) => {
    const allLogs = await db.getAll(COLLECTIONS.CUSTOMER_PAYMENT_LOGS.name);
    return allLogs.filter(
      (log) => Number(log.customer_id) === Number(customerId) && log.sync_status !== SALE_SYNC_STATUS.SYNCED_TO_CLOUD,
    );
  },

  getLocalSaleDetails: async (saleId) => {
    if (!saleId) return null;
    let localSale = null;

    // Try to filter by backend id
    const filtered = await db.filter(COLLECTIONS.SALES.name, { id: Number(saleId) });
    if (filtered && filtered.length > 0) {
      localSale = filtered[0];
    }

    if (!localSale) {
      localSale =
        (await db.get(COLLECTIONS.SALES.name, Number(saleId))) || (await db.get(COLLECTIONS.SALES.name, saleId));
    }

    if (localSale) {
      const formattedItems = (localSale.items || []).map((item) => ({
        ...item,
        quantity: item.qty,
        item: {
          name: item.name || item.item?.name || "",
          ...item.item,
        },
      }));
      return {
        ...localSale,
        id: localSale.id || localSale.local_id,
        items: formattedItems,
      };
    }
    return null;
  },

  sortJournalsDescending: (journals = []) => {
    if (!Array.isArray(journals)) return [];
    return journals.sort((a, b) => {
      const timeA = new Date(a.time).getTime() || 0;
      const timeB = new Date(b.time).getTime() || 0;
      return timeB - timeA || (Number(b.id || b.local_id) || 0) - (Number(a.id || a.local_id) || 0);
    });
  },

  saveCustomerJournalsOffline: async (customerId, journals, count) => {
    try {
      const sortedJournals = CustomerHelper.sortJournalsDescending(Array.isArray(journals) ? [...journals] : []);

      const data = {
        customerId: Number(customerId),
        journals: sortedJournals,
        count: count || sortedJournals.length,
        saved_at: Date.now(),
      };
      await db.update(COLLECTIONS.CUSTOMER_JOURNALS.name, Number(customerId), data);
    } catch (err) {
      console.error("Failed to save customer journals offline:", err);
    }
  },

  getCustomerJournalsOffline: async (customerId) => {
    try {
      const data = await db.get(COLLECTIONS.CUSTOMER_JOURNALS.name, Number(customerId));
      if (data && Array.isArray(data.journals)) {
        CustomerHelper.sortJournalsDescending(data.journals);
      }
      return data || null;
    } catch (err) {
      console.error("Failed to get customer journals offline:", err);
      return null;
    }
  },

  addOfflinePaymentToCustomerJournals: async (customerId, paymentLog, currentBalance) => {
    try {
      const cached = await CustomerHelper.getCustomerJournalsOffline(customerId);
      if (cached && Array.isArray(cached.journals)) {
        const alreadyExists = cached.journals.some((j) => j.local_id === paymentLog.local_id);
        if (alreadyExists) return;

        const newJournalEntry = {
          local_id: paymentLog.local_id,
          customer_id: Number(customerId),
          time: paymentLog.time,
          description: paymentLog.description,
          credit: Number(paymentLog.credit || 0),
          debit: Number(paymentLog.debit || 0),
          balance: Number(currentBalance || 0),
          sync_status: SALE_SYNC_STATUS.PENDING,
          type: CUSTOMER_JOURNAL_TYPES.PAYMENT,
        };

        const updatedJournals = [newJournalEntry, ...cached.journals];
        await CustomerHelper.saveCustomerJournalsOffline(customerId, updatedJournals, updatedJournals.length);
      }
    } catch (err) {
      console.error("Failed to add offline payment to cached customer journals:", err);
    }
  },

  fetchServerCustomerJournals: async (customerId, _fromDate, _toDate, filterText) => {
    let serverJournals = [];
    let serverCount = 0;
    let isOfflineMode = false;

    if (!customerId) {
      return { serverJournals, serverCount, isOfflineMode };
    }

    try {
      const params = {
        start_time: DateTimeHelpers.getStartOfDayISO(_fromDate),
        end_time: DateTimeHelpers.getEndOfDayISO(_toDate),
      };
      if (filterText) {
        params.search = filterText;
      }

      if (navigator.onLine) {
        const res = await CustomerService.getCustomerJournals(customerId, params);
        serverJournals = CustomerHelper.sortJournalsDescending(
          Array.isArray(res) ? res : res?.data || res?.results || [],
        );
        serverCount = res?.count || serverJournals.length;
        await CustomerHelper.saveCustomerJournalsOffline(customerId, serverJournals, serverCount);
      } else {
        isOfflineMode = true;
        const cachedData = await CustomerHelper.getCustomerJournalsOffline(customerId);
        serverJournals = cachedData?.journals || [];
        serverCount = cachedData?.count || serverJournals.length;
      }
    } catch (error) {
      console.log("Failed to fetch customer journals from server, using local un-synced logs:", error);
      isOfflineMode = true;
      const cachedData = await CustomerHelper.getCustomerJournalsOffline(customerId);
      serverJournals = cachedData?.journals || [];
      serverCount = cachedData?.count || serverJournals.length;
    }
    return { serverJournals, serverCount, isOfflineMode };
  },

  buildUnsyncedPaymentLogs: async (customerId, serverJournals) => {
    const unSyncedPayments = await CustomerHelper.getUnsyncedPaymentsForCustomer(customerId);
    const existingPaymentIds = new Set(serverJournals.map((journal) => journal.local_id || null).filter(Boolean));

    return unSyncedPayments
      .filter((payment) => !existingPaymentIds.has(payment.local_id))
      .map((p) => ({
        local_id: p.local_id,
        customer_id: Number(customerId),
        time: p.time,
        description: p.description,
        credit: Number(p.credit || 0),
        debit: Number(p.debit || 0),
        balance: 0,
        sync_status: p.sync_status || SALE_SYNC_STATUS.PENDING,
        type: CUSTOMER_JOURNAL_TYPES.PAYMENT,
      }));
  },

  computeCustomerUnsyncedJournals: async (customerId, serverJournals, isOfflineMode, currentCustomer) => {
    const unSyncedSales = await CustomerHelper.getUnsyncedSalesForCustomer(customerId);
    const salesLogs = await CustomerHelper.buildCustomerSalesLogs(
      customerId,
      serverJournals,
      isOfflineMode,
      unSyncedSales,
    );
    const mappedPayments = await CustomerHelper.buildUnsyncedPaymentLogs(customerId, serverJournals);

    const unsyncedLogs = [...salesLogs, ...mappedPayments].sort((a, b) => new Date(a.time) - new Date(b.time));

    const unSyncedSalesTotal = salesLogs.reduce(
      (sum, log) => sum + (Number(log.debit || 0) - Number(log.credit || 0)),
      0,
    );
    const unSyncedPaymentsTotal = mappedPayments.reduce((sum, log) => sum + log.credit, 0);

    const targetCustomer = currentCustomer || (await CustomerHelper.getCustomerByIdFromDB(customerId));
    const localBalance = Number(targetCustomer?.balance || 0);
    const syncedBalance = localBalance + unSyncedSalesTotal - unSyncedPaymentsTotal;

    let finalUpcomingBalance = serverJournals.length > 0 ? Number(serverJournals[0].balance || 0) : syncedBalance;
    unsyncedLogs.forEach((log) => {
      finalUpcomingBalance = finalUpcomingBalance - Number(log.debit || 0) + Number(log.credit || 0);
      log.balance = finalUpcomingBalance;
    });

    const combinedJournals = CustomerHelper.sortJournalsDescending([...unsyncedLogs, ...serverJournals]);

    const isUnsyncedLogs =
      unsyncedLogs.length > 0 ||
      combinedJournals.some((j) => j.sync_status && j.sync_status !== SALE_SYNC_STATUS.SYNCED_TO_CLOUD);

    return {
      finalUpcomingBalance,
      unsyncedLogs: isUnsyncedLogs,
      syncedBalance,
      combinedJournals,
    };
  },

  createCustomerSaleLog(s, isUnsynced = false) {
    return {
      local_id: s.local_id,
      time: s.time,
      description: isUnsynced ? `UN-SYNCED SALE "${s.bill_no}"` : `SALE "${s.bill_no}"`,
      credit:
        Number(s.customer_credit_used || 0) > 0
          ? Math.max(0, Number(s.payable || 0) - Number(s.customer_credit_used || 0))
          : Number(s.payable || 0),
      debit: Number(s.payable || 0),
      balance: 0,
      sync_status: s.sync_status || SALE_SYNC_STATUS.PENDING,
      type: CUSTOMER_JOURNAL_TYPES.SALE,
      sale_local_id: s.local_id,
      sale_id: s.id,
    };
  },

  filterUniqueLocalSales(localSales = [], serverJournals = []) {
    const existingSaleIds = new Set(serverJournals.map((j) => j.sale?.id || j.id).filter(Boolean));

    const existingBillNos = new Set(
      serverJournals
        .map((j) => {
          const match = j.description?.match(/"([^"]+)"/);
          return match ? match[1] : null;
        })
        .filter(Boolean),
    );

    return localSales.filter((sale) => {
      const isDuplicate =
        (sale.id && existingSaleIds.has(sale.id)) || (sale.bill_no && existingBillNos.has(sale.bill_no));

      return !isDuplicate;
    });
  },

  buildOfflineCustomerSalesLogs: async (customerId, serverJournals) => {
    const localSales = await CustomerHelper.getLocalSalesForCustomer(customerId);

    return CustomerHelper.filterUniqueLocalSales(localSales, serverJournals).map((sale) =>
      CustomerHelper.createCustomerSaleLog(sale),
    );
  },

  buildUnsyncedCustomerSalesLogs(unSyncedSales) {
    return (unSyncedSales || []).map((sale) => CustomerHelper.createCustomerSaleLog(sale, true));
  },

  buildCustomerSalesLogs: async (customerId, serverJournals, isOfflineMode, unSyncedSales) => {
    if (isOfflineMode) {
      return await CustomerHelper.buildOfflineCustomerSalesLogs(customerId, serverJournals);
    }

    return CustomerHelper.buildUnsyncedCustomerSalesLogs(unSyncedSales);
  },

  convertServerSaleToLocalSale: (serverSale) => {
    if (!serverSale) return null;

    const mappedItems = (serverSale.items || []).map((serverItem) => {
      return {
        ...serverItem,
        name: serverItem.item.name,
        qty: serverItem.quantity,
      };
    });

    return {
      ...serverSale,
      items: mappedItems,
    };
  },
};

export default CustomerHelper;
